import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Menu, ShoppingBag } from 'lucide-react';
import NavigationAccount from './access/NavigationAccount';
import NavigationTms from './access/NavigationTms';
import NavigationThemeSwitch from './NavigationThemeSwitch';
import { useSiteTheme } from './SiteTheme';
import './FloatingNavigation.css';
import './PhoneNavigation.css';

export default function PhoneNavigation({ links, itemCount, cartPulse }) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const location = useLocation();
  const theme = useSiteTheme();
  const claim = theme?.claimThemeControl, forced = theme?.themeForced;
  // The popup owns appearance even while closed, avoiding a second floating switch.
  useLayoutEffect(() => { if (!forced) return claim?.(); return undefined; }, [claim, forced]);
  useEffect(() => setOpen(false), [location.key]);
  return <Dialog.Root open={open} onOpenChange={setOpen}>
    <Dialog.Trigger asChild>
      <button type="button" className="site-floating-button phone-menu-toggle" data-testid="phone-menu-toggle" aria-label="Open menu">
        <Menu size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </Dialog.Trigger>
    <Dialog.Portal>
      <Dialog.Overlay className="phone-menu-overlay" />
      <Dialog.Content ref={contentRef} className="phone-menu-content" data-testid="phone-menu"
        onEscapeKeyDown={event => {
          // Let the existing authenticated account popover consume Escape first.
          if (contentRef.current?.querySelector('#site-account-menu')) event.preventDefault();
        }}>
        <Dialog.Title className="sr-only">Menu</Dialog.Title>
        <Dialog.Description className="sr-only">Browse the website or open TMS login, shopping cart, appearance and profile controls.</Dialog.Description>
        <div className="phone-menu-scroll">
          <nav className="phone-menu-links" aria-label="Main menu">
            {links.map(link => <Dialog.Close asChild key={link.path}>
              <Link to={link.path} aria-current={location.pathname === link.path || location.pathname.startsWith(link.path + '/') ? 'page' : undefined}>
                {link.label}
              </Link>
            </Dialog.Close>)}
          </nav>
          <div className="phone-menu-actions" aria-label="Account and appearance">
            <NavigationThemeSwitch mobile circular />
            <NavigationTms />
            <NavigationAccount />
            <Dialog.Close asChild>
              <Link to="/cart" data-testid="nav-cart" className="site-navigation-cart" title="Cart" aria-label={'Shopping cart, ' + itemCount + (itemCount === 1 ? ' item' : ' items')} aria-current={location.pathname === '/cart' ? 'page' : undefined}>
                <ShoppingBag size={21} strokeWidth={1.5} aria-hidden="true" />
                {itemCount > 0 && <span key={cartPulse} className={cartPulse ? 'site-navigation-cart-pulse' : undefined} aria-hidden="true">{itemCount}</span>}
              </Link>
            </Dialog.Close>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
